import { formatCurrencyAmount } from '@/utils/currency.utils';
import { resolveUserProfileImage, resolveUserProfileTypeWithFallback } from '@/utils/userProfileImage';

import { findArrayAtPaths, normalizeChannelId, SUPPORT_SUBSCRIPTION_PATHS } from './sourcePaths';
import { formatNextBillingLabel, formatStatus, resolveAmountValue, resolveCancelAtDate, resolveCurrency, resolveNextBillingDate } from './valueResolvers';

export function resolveSupportedUser(subscription) {
  if (!subscription || typeof subscription !== 'object') {
    return null;
  }
  const candidates = [
    subscription.supported_user,
    subscription.supportedUser,
    subscription.supported,
    subscription.supported_artist,
    subscription.supportedArtist,
    subscription.artist,
    subscription.target,
    subscription.user,
  ];

  const objectCandidates = candidates.filter((candidate) => candidate && typeof candidate === 'object');
  for (const candidate of objectCandidates) {
    const hasImage = Boolean(resolveUserProfileImage(
      candidate?.user_image,
      candidate?.profile_image,
      candidate?.userProfileImage,
      candidate?.image,
    ));
    if (hasImage) {
      return candidate;
    }
  }
  for (const candidate of objectCandidates) {
    if (candidate?.user_username || candidate?.channel_name || candidate?.user_name || candidate?.name) {
      return candidate;
    }
  }
  if (objectCandidates.length > 0) {
    return objectCandidates[0];
  }

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      const trimmed = candidate.trim();
      return { _id: trimmed, user_name: trimmed };
    }
  }

  return null;
}

export function resolveSupportFrequency(subscription) {
  if (!subscription || typeof subscription !== 'object') {
    return null;
  }
  const candidate =
    subscription.frequency ??
    subscription.interval ??
    subscription.billing_interval ??
    subscription.period ??
    subscription.cycle ??
    null;
  if (typeof candidate === 'string' && candidate.trim()) {
    return candidate.trim().toLowerCase();
  }
  return null;
}

export function resolveSupportRecurring(subscription) {
  if (!subscription || typeof subscription !== 'object') {
    return null;
  }
  if (typeof subscription.isRecurring === 'boolean') {
    return subscription.isRecurring;
  }
  if (typeof subscription.recurring === 'boolean') {
    return subscription.recurring;
  }
  if (typeof subscription.is_recurring === 'boolean') {
    return subscription.is_recurring;
  }
  const frequency = resolveSupportFrequency(subscription);
  if (!frequency) {
    return null;
  }
  return !['once', 'one-time', 'single'].includes(frequency);
}

export function formatSupportFrequencyLabel(frequency, isRecurring) {
  if (isRecurring) {
    if (frequency === 'monthly' || frequency === 'month') {
      return 'Recurring (monthly)';
    }
    if (frequency === 'yearly' || frequency === 'year') {
      return 'Recurring (annual)';
    }
    if (frequency) {
      return `Recurring (${frequency})`;
    }
    return 'Recurring';
  }
  return 'One-time';
}

export function normalizeSupportSubscription(subscription, index) {
  if (!subscription || typeof subscription !== 'object') {
    return null;
  }
  const supportedUser = resolveSupportedUser(subscription);
  const supportedUserName =
    subscription.supportedUserName ??
    subscription.supported_user_name ??
    supportedUser?.user_name ??
    supportedUser?.name ??
    supportedUser?.display_name ??
    supportedUser?.channel_name ??
    subscription.supported_artist_name ??
    subscription.artist_name ??
    'an artist';
  const supportedUsername = supportedUser?.user_username
    ? `@${supportedUser.user_username}`
    : supportedUser?.channel_name ?? '';
  const supportedUserId = normalizeChannelId(
    subscription?.supportedUserId
    ?? subscription?.supported_user_id
    ?? subscription?.supported_artist_id
    ?? subscription?.supportedArtistId
    ?? subscription?.metadata?.support_supported_id
    ?? subscription?.metadata?.supported_user_id
    ?? supportedUser?._id
    ?? supportedUser?.id
    ?? null
  );
  const supportedUserProfileImage = resolveUserProfileImage(
    subscription?.supportedUserProfileImage,
    subscription?.supported_user_image,
    subscription?.supportedUserImage,
    subscription?.supported_user_image_id,
    subscription?.supportedUserImageId,
    subscription?.supported?.user_image,
    subscription?.supportedUser?.user_image,
    subscription?.supported_user?.user_image,
    subscription?.supported_artist?.user_image,
    subscription?.artist?.user_image,
    subscription?.metadata?.support_supported_user_image,
    subscription?.metadata?.support_supported_image,
    subscription?.metadata?.supported_user_image,
    subscription?.metadata?.supported_user_image_id,
    supportedUser?.user_image,
    supportedUser,
  );
  const supportedUserProfileType = resolveUserProfileTypeWithFallback(
    'collector',
    subscription?.supportedUserProfileType,
    subscription?.supported_user_type,
    subscription?.supportedUserType,
    subscription?.supported_artist_type,
    subscription?.supportedArtistType,
    supportedUser?.user_type,
    supportedUser?.__t,
    supportedUser,
  );
  const rawSupportAmount = Number(
    subscription?.support_amount
    ?? subscription?.amount
    ?? subscription?.price
    ?? NaN
  );
  const supportAmount = Number.isFinite(rawSupportAmount)
    ? rawSupportAmount
    : resolveAmountValue(subscription);
  const rawChargedAmount = Number(
    subscription?.charged_amount
    ?? subscription?.order_payment?.total
    ?? NaN
  );
  const chargedAmount = Number.isFinite(rawChargedAmount)
    ? rawChargedAmount
    : supportAmount;
  const currencyCode = resolveCurrency(subscription);
  const isRecurring = resolveSupportRecurring(subscription);
  const frequency = resolveSupportFrequency(subscription);
  const frequencyLabel = formatSupportFrequencyLabel(frequency, isRecurring);
  const nextBillingLabel = subscription
    ? formatNextBillingLabel(resolveNextBillingDate(subscription))
    : 'Billing date not available';
  const cancelAtPeriodEnd = Boolean(
    subscription.cancel_at_period_end
    ?? subscription.cancelAtPeriodEnd
    ?? subscription.cancel_at_period_end
    ?? subscription.cancelAtPeriodEndDate
    ?? subscription.cancel_at_period_end_date
  );
  const cancelAtDate = resolveCancelAtDate(subscription);
  const rawStatus = subscription.status ?? subscription.state ?? null;
  const normalizedStatus = typeof rawStatus === 'string'
    ? rawStatus.toLowerCase().trim()
    : '';
  const hasStatus = normalizedStatus.length > 0;
  const hasActiveStatus =
    normalizedStatus === 'active' || normalizedStatus === 'active_subscription';
  const isActive = Boolean(
    subscription.isActive
    ?? subscription.is_active
    ?? (hasStatus ? hasActiveStatus : isRecurring)
  );
  const isCanceling = cancelAtPeriodEnd || normalizedStatus === 'canceling' || normalizedStatus === 'canceled';
  const statusLabel = isCanceling ? 'Cancelled' : formatStatus(subscription, isActive);

  const supportAmountLabel = Number.isFinite(supportAmount)
    ? formatCurrencyAmount(supportAmount, currencyCode, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    : null;
  const chargedAmountLabel = Number.isFinite(chargedAmount)
    ? formatCurrencyAmount(chargedAmount, currencyCode, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    : null;

  return {
    id: subscription.id ?? subscription._id ?? subscription.subscriptionId ?? `support-tech-${index}`,
    supportedUser,
    supportedUserId,
    supportedUserProfileImage,
    supportedUserProfileType,
    supportedUserName,
    supportedUserSubtitle: supportedUsername,
    amountLabel: chargedAmountLabel,
    supportAmountLabel,
    chargedAmountLabel,
    frequencyLabel,
    isRecurring: Boolean(isRecurring),
    nextBillingLabel: cancelAtDate ? formatNextBillingLabel(cancelAtDate) : nextBillingLabel,
    statusLabel,
    isActive,
    isCanceling,
    billing_mode:
      subscription?.billing_mode
      ?? subscription?.metadata?.billing_mode
      ?? null,
    billing_is_business:
      subscription?.billing_is_business
      ?? subscription?.metadata?.billing_is_business
      ?? null,
  };
}

export function extractSupportSubscriptions(profileData) {
  if (!profileData) {
    return [];
  }
  const raw = findArrayAtPaths(profileData, SUPPORT_SUBSCRIPTION_PATHS);
  if (raw.length === 0) {
    return [];
  }
  return raw
    .map(normalizeSupportSubscription)
    .filter(Boolean);
}
